import React, { useState, useMemo } from 'react';
import { Plus, Users, Edit, Trash2, List, LayoutGrid, ChevronRight, Search, X, FileText, FileSpreadsheet, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const PERFORMANCE_TYPES = ["DYN", "DNF", "STA", "DYNBF", "CNF", "CWT", "FIM", "CWTBF", "16x25m", "16x50m"];
const POOL_TYPES = ["25m", "50m", "Open Water", "Ocean", "Quarry", "Lake", "River", "Indoor Pool", "Outdoor Pool", "Other"];

const FollowUpTab = ({
  freedivers,
  coaches,
  selectedFreediver,
  freediverPerformances,
  onSelectFreediver,
  onCreatePerformance,
  onUpdatePerformance,
  onDeletePerformance,
  t
}) => {
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPerformance, setEditingPerformance] = useState(null);
  
  // Translation helper
  const translate = (key, fallback) => t ? t(key) : fallback;
  const [form, setForm] = useState({
    freediver_id: '',
    performance_date: new Date().toISOString().split('T')[0],
    location: '',
    pool_type: '25m',
    coach_id: '',
    performance_type: 'DYN',
    distance: '',
    duration_minutes: '',
    duration_secs: '',
    comments: '',
    preparation_protocol: ''
  });

  // Filter freedivers based on search query
  const filteredFreedivers = useMemo(() => {
    if (!searchQuery.trim()) return freedivers;
    const query = searchQuery.toLowerCase();
    return freedivers.filter(freediver => {
      const fullName = `${freediver.first_name} ${freediver.last_name}`.toLowerCase();
      return fullName.includes(query) || 
        freediver.nationality?.toLowerCase().includes(query) ||
        freediver.email?.toLowerCase().includes(query);
    });
  }, [freedivers, searchQuery]);

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Format duration seconds to minutes:seconds
  const formatPerformanceDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!selectedFreediver || !freediverPerformances.length) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Helper: get display value
    const getPerfValue = (perf) => {
      if (perf.performance_type === 'STA') {
        return perf.duration_seconds ? formatPerformanceDuration(perf.duration_seconds) : '-';
      }
      return perf.distance ? `${perf.distance}m` : '-';
    };

    // Sort by performance_type then by date
    const sortedPerfs = [...freediverPerformances].sort((a, b) => {
      if (a.performance_type < b.performance_type) return -1;
      if (a.performance_type > b.performance_type) return 1;
      return new Date(a.performance_date) - new Date(b.performance_date);
    });

    // Title
    doc.setFontSize(20);
    doc.setTextColor(0, 51, 102);
    doc.text(`${translate('performanceHistory', 'Performance History')}`, pageWidth / 2, 20, { align: 'center' });

    // Freediver info
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(`${selectedFreediver.first_name} ${selectedFreediver.last_name}`, pageWidth / 2, 32, { align: 'center' });

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    const freediverInfo = [
      selectedFreediver.nationality,
      selectedFreediver.gender,
      selectedFreediver.email
    ].filter(Boolean).join(' • ');
    doc.text(freediverInfo, pageWidth / 2, 38, { align: 'center' });

    // Build table rows with blank separators between event types
    const headers = [
      translate('date', 'Date'),
      translate('type', 'Type'),
      translate('performance', 'Performance'),
      translate('location', 'Location'),
      translate('poolType', 'Pool Type'),
      translate('ranking', 'Ranking'),
      translate('notes', 'Notes')
    ];

    const tableBody = [];
    let lastType = null;
    sortedPerfs.forEach(perf => {
      if (lastType !== null && perf.performance_type !== lastType) {
        // Blank separator row
        tableBody.push(['', '', '', '', '', '', '']);
      }
      tableBody.push([
        formatDate(perf.performance_date),
        perf.performance_type,
        getPerfValue(perf),
        perf.location || '-',
        perf.pool_type || '-',
        perf.ranking ? `#${perf.ranking}` : '-',
        perf.comments || '-'
      ]);
      lastType = perf.performance_type;
    });

    autoTable(doc, {
      startY: 45,
      head: [headers],
      body: tableBody,
      theme: 'striped',
      headStyles: { fillColor: [0, 51, 102], textColor: 255 },
      styles: { fontSize: 8, cellPadding: 2 },
      didParseCell: (data) => {
        // Blank separator rows — no fill, no borders
        if (data.row.raw && data.row.raw.every(c => c === '')) {
          data.cell.styles.fillColor = [255, 255, 255];
          data.cell.styles.lineWidth = 0;
          data.cell.styles.minCellHeight = 4;
        }
      },
      columnStyles: {
        6: { cellWidth: 40 }
      }
    });

    // Footer with statistics
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.setTextColor(0, 51, 102);
    doc.text(`${translate('totalPerformances', 'Total Performances')}: ${freediverPerformances.length}`, 14, finalY);

    // Export date
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`${translate('exportedOn', 'Exported on')}: ${new Date().toLocaleString()}`, 14, doc.internal.pageSize.getHeight() - 10);

    // Save
    doc.save(`${selectedFreediver.first_name}_${selectedFreediver.last_name}_performances.pdf`);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!selectedFreediver || !freediverPerformances.length) return;

    // Prepare data — sort by type then date
    const sortedPerfs = [...freediverPerformances].sort((a, b) => {
      if (a.performance_type < b.performance_type) return -1;
      if (a.performance_type > b.performance_type) return 1;
      return new Date(a.performance_date) - new Date(b.performance_date);
    });

    const getPerfValue = (perf) => {
      if (perf.performance_type === 'STA') {
        return perf.duration_seconds ? formatPerformanceDuration(perf.duration_seconds) : '-';
      }
      return perf.distance ? `${perf.distance}m` : '-';
    };

    const data = [];
    let lastType = null;
    sortedPerfs.forEach(perf => {
      if (lastType !== null && perf.performance_type !== lastType) {
        data.push({}); // blank row between event types
      }
      data.push({
        [translate('date', 'Date')]: formatDate(perf.performance_date),
        [translate('type', 'Type')]: perf.performance_type,
        [translate('performance', 'Performance')]: getPerfValue(perf),
        [translate('location', 'Location')]: perf.location || '-',
        [translate('poolType', 'Pool Type')]: perf.pool_type || '-',
        [translate('ranking', 'Ranking')]: perf.ranking || '-',
        [translate('coach', 'Coach')]: perf.coach_name || '-',
        [translate('notes', 'Notes')]: perf.comments || '-'
      });
      lastType = perf.performance_type;
    });

    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Add freediver info as header (will be prepended)
    const headerData = [
      [`${translate('performanceHistory', 'Performance History')}`],
      [`${translate('freediver', 'Freediver')}: ${selectedFreediver.first_name} ${selectedFreediver.last_name}`],
      [`${translate('nationality', 'Nationality')}: ${selectedFreediver.nationality || '-'}`],
      [`${translate('email', 'Email')}: ${selectedFreediver.email || '-'}`],
      [`${translate('exportedOn', 'Exported on')}: ${new Date().toLocaleString()}`],
      [] // Empty row before data
    ];
    
    // Create final worksheet with header + data
    const wsWithHeader = XLSX.utils.aoa_to_sheet(headerData);
    XLSX.utils.sheet_add_json(wsWithHeader, data, { origin: 'A7' });
    
    // Set column widths
    wsWithHeader['!cols'] = [
      { wch: 12 }, // Date
      { wch: 10 }, // Type
      { wch: 12 }, // Performance
      { wch: 20 }, // Location
      { wch: 12 }, // Pool Type
      { wch: 8 },  // Ranking
      { wch: 15 }, // Coach
      { wch: 30 }  // Notes
    ];

    XLSX.utils.book_append_sheet(wb, wsWithHeader, translate('performances', 'Performances'));
    
    // Save
    XLSX.writeFile(wb, `${selectedFreediver.first_name}_${selectedFreediver.last_name}_performances.xlsx`);
  };

  // Get ranking icon
  const getRankingIcon = (ranking) => {
    if (ranking === 1) return '🥇';
    if (ranking === 2) return '🥈';
    if (ranking === 3) return '🥉';
    return '';
  };

  // Group performances by type and calculate stats per type
  const getPerformanceStatsByType = (performances) => {
    const byType = {};
    performances.forEach(perf => {
      const type = perf.performance_type;
      if (!byType[type]) {
        byType[type] = { 
          gold: 0, silver: 0, bronze: 0, total: 0, 
          goldValue: null, silverValue: null, bronzeValue: null,
          goldDuration: null, silverDuration: null, bronzeDuration: null
        };
      }
      byType[type].total++;
      
      if (perf.ranking === 1) {
        byType[type].gold++;
        byType[type].goldValue = type === 'STA' ? null : perf.distance;
        byType[type].goldDuration = perf.duration_seconds;
      }
      if (perf.ranking === 2) {
        byType[type].silver++;
        byType[type].silverValue = type === 'STA' ? null : perf.distance;
        byType[type].silverDuration = perf.duration_seconds;
      }
      if (perf.ranking === 3) {
        byType[type].bronze++;
        byType[type].bronzeValue = type === 'STA' ? null : perf.distance;
        byType[type].bronzeDuration = perf.duration_seconds;
      }
    });
    return byType;
  };

  const resetForm = () => {
    const today = new Date().toISOString().split('T')[0];
    setForm({
      freediver_id: '',
      performance_date: today,
      location: '',
      pool_type: '25m',
      coach_id: '',
      performance_type: 'DYN',
      distance: '',
      duration_minutes: '',
      duration_secs: '',
      comments: '',
      preparation_protocol: ''
    });
  };

  const openNewModal = (freediverId = '') => {
    setEditingPerformance(null);
    resetForm();
    const today = new Date().toISOString().split('T')[0];
    if (freediverId) {
      setForm(prev => ({ ...prev, freediver_id: freediverId, performance_date: today }));
    } else {
      setForm(prev => ({ ...prev, performance_date: today }));
    }
    setShowModal(true);
  };

  const openEditModal = (performance) => {
    setEditingPerformance(performance);
    const mins = Math.floor((performance.duration_seconds || 0) / 60);
    const secs = (performance.duration_seconds || 0) % 60;
    
    setForm({
      freediver_id: performance.freediver_id,
      performance_date: performance.performance_date?.split('T')[0] || '',
      location: performance.location || '',
      pool_type: performance.pool_type || '25m',
      coach_id: performance.coach_id || '',
      performance_type: performance.performance_type || 'DYN',
      distance: performance.distance?.toString() || '',
      duration_minutes: mins.toString(),
      duration_secs: secs.toString(),
      comments: performance.comments || '',
      preparation_protocol: performance.preparation_protocol || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    try {
      const durationSeconds = (parseInt(form.duration_minutes) || 0) * 60 + (parseInt(form.duration_secs) || 0);
      
      const coach = coaches.find(c => c.id === form.coach_id);
      const coachName = coach ? `${coach.first_name} ${coach.last_name}` : '';
      
      const performanceData = {
        freediver_id: form.freediver_id,
        performance_date: new Date(form.performance_date).toISOString(),
        location: form.location,
        pool_type: form.pool_type,
        coach_id: form.coach_id || null,
        coach_name: coachName,
        performance_type: form.performance_type,
        distance: form.performance_type === 'STA' ? null : parseFloat(form.distance) || 0,
        duration_seconds: durationSeconds,
        comments: form.comments,
        preparation_protocol: form.preparation_protocol
      };

      if (editingPerformance) {
        await onUpdatePerformance(editingPerformance.id, performanceData);
      } else {
        await onCreatePerformance(performanceData);
      }
      
      // Close modal and reset form after successful save
      setShowModal(false);
      setEditingPerformance(null);
      resetForm();
    } catch (error) {
      console.error('Error saving performance:', error);
      // Modal stays open on error so user can retry
    }
  };

  const handleDelete = async (performanceId) => {
    if (!window.confirm('Are you sure you want to delete this performance? This action cannot be undone.')) {
      return;
    }
    await onDeletePerformance(performanceId);
  };

  const performanceStats = getPerformanceStatsByType(freediverPerformances);

  return (
    <div className="space-y-4">
      {/* Header with Title and View Toggle */}
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-blue-200">{translate('followUpTitle', 'Follow Up - Freediver Tracking')}</h2>
        <div className="flex items-center space-x-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400" size={18} />
            <Input
              type="text"
              placeholder={translate('searchFreediver', 'Search freediver...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 w-64 bg-blue-800/50 border-blue-600 text-white placeholder-blue-400 focus:border-teal-500"
              data-testid="followup-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-400 hover:text-white"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {/* View Mode Toggle */}
          <div className="flex bg-blue-800/50 rounded-lg p-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'bg-teal-600' : 'text-blue-300 hover:text-white'}
              data-testid="followup-grid-view-btn"
            >
              <LayoutGrid size={18} />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-teal-600' : 'text-blue-300 hover:text-white'}
              data-testid="followup-list-view-btn"
            >
              <List size={18} />
            </Button>
          </div>
          <Button 
            onClick={() => openNewModal()} 
            className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700"
            data-testid="add-performance-btn"
          >
            <Plus className="mr-2" size={18} />
            {translate('addPerformance', 'Add Performance')}
          </Button>
        </div>
      </div>

      {/* Main Content - Side by Side Layout */}
      <div className="flex gap-6">
        {/* Left Panel - Freediver Selection */}
        <div className={`${selectedFreediver ? 'w-1/3' : 'w-full'} transition-all duration-300`}>
          <Card className="bg-gradient-to-r from-blue-900/50 to-teal-900/50 border-blue-700/30 h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-blue-200 text-lg">{translate('selectFreediverToTrack', 'Select Freediver to Track')}</CardTitle>
            </CardHeader>
            <CardContent className="max-h-[calc(100vh-280px)] overflow-y-auto">
              {viewMode === 'grid' ? (
                /* Grid View */
                <div className={`grid ${selectedFreediver ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4 lg:grid-cols-6'} gap-3`}>
                  {filteredFreedivers.map((freediver) => (
                    <Card 
                      key={freediver.id}
                      data-testid={`freediver-card-${freediver.id}`}
                      className={`cursor-pointer transition-all hover:scale-105 ${
                        selectedFreediver?.id === freediver.id 
                          ? 'bg-teal-700/50 border-teal-400 border-2' 
                          : 'bg-blue-800/30 border-blue-700/30 hover:bg-blue-700/40'
                      }`}
                      onClick={() => onSelectFreediver(freediver.id)}
                    >
                      <CardContent className="p-3 text-center">
                        {freediver.photo_url ? (
                          <img 
                            src={freediver.photo_url} 
                            alt={`${freediver.first_name} ${freediver.last_name}`}
                            className="w-12 h-12 rounded-full mx-auto mb-2 object-cover border-2 border-blue-500/50"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full mx-auto mb-2 bg-blue-700/50 flex items-center justify-center border-2 border-blue-500/50">
                            <Users size={20} className="text-blue-300" />
                          </div>
                        )}
                        <p className="text-white font-semibold text-xs truncate">
                          {freediver.first_name} {freediver.last_name}
                        </p>
                        <p className="text-blue-300 text-xs">{freediver.nationality}</p>
                        {freediver.date_of_birth && (
                          <Badge variant="outline" className="mt-1 border-blue-500/50 text-blue-200 text-xs">
                            {calculateAge(freediver.date_of_birth)} {translate('yrsShort', 'yrs')}
                          </Badge>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                /* List View */
                <div className="space-y-2">
                  {filteredFreedivers.map((freediver) => (
                    <div
                      key={freediver.id}
                      data-testid={`freediver-list-item-${freediver.id}`}
                      className={`flex items-center p-3 rounded-lg cursor-pointer transition-all ${
                        selectedFreediver?.id === freediver.id 
                          ? 'bg-teal-700/50 border-2 border-teal-400' 
                          : 'bg-blue-800/30 hover:bg-blue-700/40 border border-transparent'
                      }`}
                      onClick={() => onSelectFreediver(freediver.id)}
                    >
                      {freediver.photo_url ? (
                        <img 
                          src={freediver.photo_url} 
                          alt={`${freediver.first_name} ${freediver.last_name}`}
                          className="w-10 h-10 rounded-full object-cover border-2 border-blue-500/50 mr-3"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-blue-700/50 flex items-center justify-center border-2 border-blue-500/50 mr-3">
                          <Users size={18} className="text-blue-300" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-sm truncate">
                          {freediver.first_name} {freediver.last_name}
                        </p>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-blue-300">{freediver.nationality}</span>
                          {freediver.date_of_birth && (
                            <span className="text-blue-400">• {calculateAge(freediver.date_of_birth)} yrs</span>
                          )}
                        </div>
                      </div>
                      {selectedFreediver?.id === freediver.id && (
                        <ChevronRight className="text-teal-400" size={20} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Selected Freediver Details & Performances */}
        {selectedFreediver && (
          <div className="w-2/3 transition-all duration-300">
            <Card className="bg-gradient-to-r from-teal-900/50 to-blue-900/50 border-teal-700/30 h-full">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center space-x-4">
                    {selectedFreediver.photo_url ? (
                      <img 
                        src={selectedFreediver.photo_url} 
                        alt={`${selectedFreediver.first_name} ${selectedFreediver.last_name}`}
                        className="w-16 h-16 rounded-full object-cover border-3 border-teal-400"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-teal-700/50 flex items-center justify-center border-3 border-teal-400">
                        <Users size={28} className="text-teal-300" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-teal-200 text-xl">
                        {selectedFreediver.first_name} {selectedFreediver.last_name}
                      </CardTitle>
                      <div className="flex flex-wrap gap-2 mt-1">
                        <Badge className="bg-teal-700/50 text-teal-200 text-xs">
                          {selectedFreediver.nationality}
                        </Badge>
                        {selectedFreediver.date_of_birth && (
                          <Badge className="bg-blue-700/50 text-blue-200 text-xs">
                            {calculateAge(selectedFreediver.date_of_birth)} {translate('yearsOld', 'years old')}
                          </Badge>
                        )}
                        {selectedFreediver.certification_agency && (
                          <Badge className="bg-orange-700/50 text-orange-200 text-xs">
                            {selectedFreediver.certification_agency}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button 
                    onClick={() => openNewModal(selectedFreediver.id)} 
                    className="bg-teal-600 hover:bg-teal-700"
                    size="sm"
                  >
                    <Plus className="mr-1" size={14} />
                    {translate('addPerformance', 'Add Performance')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="max-h-[calc(100vh-320px)] overflow-y-auto">
                {/* Performance Statistics Per Type */}
                <div className="mb-4">
                  <h4 className="text-blue-200 font-semibold mb-3 text-sm">{translate('perfSummaryByType', 'Performance Summary by Type')}</h4>
                  {Object.keys(performanceStats).length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                      {Object.entries(performanceStats).map(([type, stats]) => (
                        <div key={type} className="p-3 bg-blue-800/30 rounded-lg border border-blue-700/30">
                          <div className="flex justify-between items-center mb-2">
                            <Badge className="bg-teal-700/50 text-teal-200">{type}</Badge>
                            <span className="text-blue-300 text-xs">{stats.total} perf.</span>
                          </div>
                          <div className="space-y-1">
                            {/* Gold */}
                            <div className="flex items-center justify-between text-sm bg-yellow-900/20 rounded px-2 py-1">
                              <span className="flex items-center gap-1">
                                <span>🥇</span>
                                <span className="text-yellow-300 font-bold">{stats.gold}</span>
                              </span>
                              {stats.goldValue !== null || stats.goldDuration ? (
                                <span className="text-yellow-200 text-xs">
                                  {type === 'STA' 
                                    ? formatPerformanceDuration(stats.goldDuration)
                                    : `${stats.goldValue}m - ${formatPerformanceDuration(stats.goldDuration)}`
                                  }
                                </span>
                              ) : (
                                <span className="text-yellow-400/50 text-xs">-</span>
                              )}
                            </div>
                            {/* Silver */}
                            <div className="flex items-center justify-between text-sm bg-gray-700/20 rounded px-2 py-1">
                              <span className="flex items-center gap-1">
                                <span>🥈</span>
                                <span className="text-gray-300 font-bold">{stats.silver}</span>
                              </span>
                              {stats.silverValue !== null || stats.silverDuration ? (
                                <span className="text-gray-200 text-xs">
                                  {type === 'STA' 
                                    ? formatPerformanceDuration(stats.silverDuration)
                                    : `${stats.silverValue}m - ${formatPerformanceDuration(stats.silverDuration)}`
                                  }
                                </span>
                              ) : (
                                <span className="text-gray-400/50 text-xs">-</span>
                              )}
                            </div>
                            {/* Bronze */}
                            <div className="flex items-center justify-between text-sm bg-orange-900/20 rounded px-2 py-1">
                              <span className="flex items-center gap-1">
                                <span>🥉</span>
                                <span className="text-orange-300 font-bold">{stats.bronze}</span>
                              </span>
                              {stats.bronzeValue !== null || stats.bronzeDuration ? (
                                <span className="text-orange-200 text-xs">
                                  {type === 'STA' 
                                    ? formatPerformanceDuration(stats.bronzeDuration)
                                    : `${stats.bronzeValue}m - ${formatPerformanceDuration(stats.bronzeDuration)}`
                                  }
                                </span>
                              ) : (
                                <span className="text-orange-400/50 text-xs">-</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 gap-3">
                      <div className="text-center p-3 bg-blue-800/30 rounded">
                        <p className="text-2xl font-bold text-white">{freediverPerformances.length}</p>
                        <p className="text-blue-300 text-xs">Total</p>
                      </div>
                      <div className="text-center p-3 bg-yellow-800/30 rounded">
                        <p className="text-2xl font-bold text-yellow-200">0</p>
                        <p className="text-yellow-300 text-xs">{translate('gold', 'Gold')}</p>
                      </div>
                      <div className="text-center p-3 bg-gray-700/30 rounded">
                        <p className="text-2xl font-bold text-gray-200">0</p>
                        <p className="text-gray-300 text-xs">{translate('silver', 'Silver')}</p>
                      </div>
                      <div className="text-center p-3 bg-orange-800/30 rounded">
                        <p className="text-2xl font-bold text-orange-200">0</p>
                        <p className="text-orange-300 text-xs">{translate('bronze', 'Bronze')}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Performance List */}
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-blue-200 font-semibold text-sm">{translate('performanceHistory', 'Performance History')}</h4>
                  {freediverPerformances.length > 0 && (
                    <div className="flex gap-2">
                      <Button
                        onClick={exportToPDF}
                        size="sm"
                        variant="outline"
                        className="border-red-500 text-red-300 hover:bg-red-700/50 h-7 px-2"
                        title={translate('exportPdf', 'Export PDF')}
                      >
                        <FileText size={14} className="mr-1" />
                        PDF
                      </Button>
                      <Button
                        onClick={exportToExcel}
                        size="sm"
                        variant="outline"
                        className="border-green-500 text-green-300 hover:bg-green-700/50 h-7 px-2"
                        title={translate('exportExcel', 'Export Excel')}
                      >
                        <FileSpreadsheet size={14} className="mr-1" />
                        Excel
                      </Button>
                    </div>
                  )}
                </div>
                {freediverPerformances.length > 0 ? (
                  <div className="space-y-2">
                    {freediverPerformances.map((perf) => (
                      <Card key={perf.id} className="bg-blue-800/20 border-blue-700/30" data-testid={`performance-card-${perf.id}`}>
                        <CardContent className="p-3">
                          <div className="flex justify-between items-start">
                            <div className="flex items-center space-x-3">
                              {/* Ranking Icon */}
                              <div className="text-2xl">
                                {getRankingIcon(perf.ranking) || '📊'}
                              </div>
                              <div>
                                <div className="flex items-center space-x-2 flex-wrap">
                                  <Badge className="bg-teal-700/50 text-teal-200 text-xs">
                                    {perf.performance_type}
                                  </Badge>
                                  {perf.performance_type !== 'STA' && perf.distance && (
                                    <span className="text-white font-bold">{perf.distance} m</span>
                                  )}
                                  <span className="text-blue-300 text-sm">
                                    ⏱️ {formatPerformanceDuration(perf.duration_seconds)}
                                  </span>
                                </div>
                                <p className="text-blue-300 text-xs mt-1">
                                  📅 {new Date(perf.performance_date).toLocaleDateString('en-GB')} • 
                                  📍 {perf.location} • 🏊 {perf.pool_type}
                                </p>
                                {perf.coach_name && (
                                  <p className="text-blue-400 text-xs">👨‍🏫 {perf.coach_name}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex space-x-1">
                              <Button
                                onClick={() => openEditModal(perf)}
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-blue-300 hover:bg-blue-700/50"
                                data-testid={`edit-performance-${perf.id}`}
                              >
                                <Edit size={14} />
                              </Button>
                              <Button
                                onClick={() => handleDelete(perf.id)}
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-red-300 hover:bg-red-700/50"
                                data-testid={`delete-performance-${perf.id}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Alert className="bg-blue-900/30 border-blue-700/50">
                    <AlertDescription className="text-blue-200 text-sm">
                      {translate('noPerformancesYet', 'No performances recorded yet. Click "Add Performance" to start tracking.')}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Performance Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-gradient-to-br from-blue-900 to-teal-900 border-blue-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-teal-200">
              {editingPerformance ? translate('editPerformance', 'Edit Performance') : translate('addNewPerformance', 'Add New Performance')}
            </DialogTitle>
            <DialogDescription className="text-blue-300">
              {editingPerformance ? translate('updatePerfDetails', 'Update the performance details.') : translate('recordNewPerf', 'Record a new performance for tracking.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Freediver Selection */}
            <div>
              <Label className="text-blue-200">{translate('freediver', 'Freediver')}</Label>
              <Select 
                value={form.freediver_id} 
                onValueChange={(value) => setForm({...form, freediver_id: value})}
              >
                <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white" data-testid="performance-freediver-select">
                  <SelectValue placeholder={translate('selectFreediver', 'Select freediver')} />
                </SelectTrigger>
                <SelectContent className="bg-blue-800 border-blue-600">
                  {freedivers.map((fd) => (
                    <SelectItem key={fd.id} value={fd.id}>
                      {fd.first_name} {fd.last_name} ({fd.nationality})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-blue-200">{translate('performanceDate', 'Performance Date')}</Label>
                <Input
                  type="date"
                  value={form.performance_date}
                  onChange={(e) => setForm({...form, performance_date: e.target.value})}
                  className="bg-blue-800/50 border-blue-600 text-white"
                  data-testid="performance-date-input"
                />
              </div>
              <div>
                <Label className="text-blue-200">{translate('performanceType', 'Performance Type')}</Label>
                <Select 
                  value={form.performance_type} 
                  onValueChange={(value) => setForm({...form, performance_type: value})}
                >
                  <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white" data-testid="performance-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-blue-800 border-blue-600">
                    {PERFORMANCE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-blue-200">{translate('location', 'Location')}</Label>
                <Input
                  value={form.location}
                  onChange={(e) => setForm({...form, location: e.target.value})}
                  className="bg-blue-800/50 border-blue-600 text-white"
                  placeholder={translate('locationPlaceholder', 'e.g., Paris Swimming Center')}
                  data-testid="performance-location-input"
                />
              </div>
              <div>
                <Label className="text-blue-200">{translate('poolType', 'Pool Type')}</Label>
                <Select 
                  value={form.pool_type} 
                  onValueChange={(value) => setForm({...form, pool_type: value})}
                >
                  <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-blue-800 border-blue-600">
                    {POOL_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.performance_type !== 'STA' && (
              <div>
                <Label className="text-blue-200">{translate('distanceMeters', 'Distance (meters)')}</Label>
                <Input
                  type="number"
                  value={form.distance}
                  onChange={(e) => setForm({...form, distance: e.target.value})}
                  className="bg-blue-800/50 border-blue-600 text-white"
                  placeholder={translate('distancePlaceholder', 'e.g., 150')}
                  data-testid="performance-distance-input"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-blue-200">{translate('durationMinutes', 'Duration (Minutes)')}</Label>
                <Input
                  type="number"
                  value={form.duration_minutes}
                  onChange={(e) => setForm({...form, duration_minutes: e.target.value})}
                  className="bg-blue-800/50 border-blue-600 text-white"
                  placeholder="0"
                  data-testid="performance-duration-minutes"
                />
              </div>
              <div>
                <Label className="text-blue-200">{translate('durationSeconds', 'Duration (Seconds)')}</Label>
                <Input
                  type="number"
                  value={form.duration_secs}
                  onChange={(e) => setForm({...form, duration_secs: e.target.value})}
                  className="bg-blue-800/50 border-blue-600 text-white"
                  placeholder="0"
                  max="59"
                  data-testid="performance-duration-seconds"
                />
              </div>
            </div>

            <div>
              <Label className="text-blue-200">{translate('coachTrainer', 'Coach/Trainer')}</Label>
              <Select
                value={form.coach_id || 'none'}
                onValueChange={(value) => setForm({...form, coach_id: value === 'none' ? '' : value})}
              >
                <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white" data-testid="performance-coach-select">
                  <SelectValue placeholder={translate('selectCoach', 'Select a coach/trainer')} />
                </SelectTrigger>
                <SelectContent className="bg-blue-800 border-blue-600">
                  <SelectItem value="none">{translate('noCoachSelected', 'No coach selected')}</SelectItem>
                  {coaches.map((coach) => (
                    <SelectItem key={coach.id} value={coach.id}>
                      {coach.first_name} {coach.last_name} ({coach.nationality})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {coaches.length === 0 && (
                <p className="text-xs text-blue-400 mt-1">
                  {translate('noCoachesAvailable', 'No coaches available. Mark a freediver as "Coach/Trainer" in the Freedivers section.')}
                </p>
              )}
            </div>

            <div>
              <Label className="text-blue-200">{translate('commentsObservations', 'Comments/Observations')}</Label>
              <Textarea
                value={form.comments}
                onChange={(e) => setForm({...form, comments: e.target.value})}
                className="bg-blue-800/50 border-blue-600 text-white"
                placeholder={translate('notesPlaceholder', 'Notes about the performance...')}
                rows={2}
              />
            </div>

            <div>
              <Label className="text-blue-200">{translate('preparationProtocol', 'Preparation Protocol')}</Label>
              <Textarea
                value={form.preparation_protocol}
                onChange={(e) => setForm({...form, preparation_protocol: e.target.value})}
                className="bg-blue-800/50 border-blue-600 text-white"
                placeholder={translate('protocolPlaceholder', 'Detailed preparation protocol...')}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowModal(false);
                setEditingPerformance(null);
                resetForm();
              }}
              className="border-gray-500 text-gray-300 hover:bg-gray-700/50"
            >
              {translate('cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700"
              data-testid="save-performance-btn"
            >
              {editingPerformance ? translate('updatePerformance', 'Update Performance') : translate('addPerformance', 'Add Performance')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FollowUpTab;
